import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import {
  BILL_STATUS,
  FINANCIAL_RECORD_TYPE,
  WITHHOLDING_STATUS,
} from "constants/vendorBills";
import { VendorBill } from "types/Models/vendorBills";

export const ActionItems = ({
  bill,
  onStatusChange,
  onSendRequest,
  onDownload,
  onDelete,
  onDuplicate,
  onEdit,
  hideMarkAsActive,
}: {
  bill: VendorBill;
  onDelete: () => void;
  onDownload: () => void;
  onSendRequest: () => void;
  onStatusChange: (args: { status: keyof typeof BILL_STATUS }) => void;
  onDuplicate: () => void;
  onEdit?: () => void;
  hideMarkAsActive?: boolean;
}) => {
  const isBill = bill.bill_document_type === FINANCIAL_RECORD_TYPE.BILL;
  const status = isBill ? bill.bill_payment_status : bill.bill_request_status;

  const statusItems = (() => {
    switch (status) {
      case BILL_STATUS.DRAFT: {
        return (
          <>
            {isBill ? (
              <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                <Dropdown.Item
                  onSelect={() => onStatusChange({ status: BILL_STATUS.DUE })}
                  hidden={hideMarkAsActive}
                >
                  Mark as Active
                </Dropdown.Item>
              </LockedUi>
            ) : (
              <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                <Dropdown.Item onSelect={onSendRequest}>
                  Send Request
                </Dropdown.Item>
              </LockedUi>
            )}
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Delete" />}
            >
              <Dropdown.Item type="danger" onSelect={onDelete}>
                Delete
              </Dropdown.Item>
            </LockedUi>
          </>
        );
      }
      case BILL_STATUS.DUE: {
        return (
          <>
            <Dropdown.Item
              onSelect={() => onStatusChange({ status: BILL_STATUS.PAID })}
            >
              Record payment
            </Dropdown.Item>
            {onEdit && <Dropdown.Item onSelect={onEdit}>Edit</Dropdown.Item>}
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Delete" />}
            >
              <Dropdown.Item type="danger" onSelect={onDelete}>
                Delete
              </Dropdown.Item>
            </LockedUi>
          </>
        );
      }
      case BILL_STATUS.PARTIALLY_PAID: {
        return (
          <>
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item
                onSelect={() => onStatusChange({ status: BILL_STATUS.PAID })}
              >
                Record payment
              </Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
          </>
        );
      }

      case BILL_STATUS.OVERDUE: {
        return (
          <>
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item
                onSelect={() => onStatusChange({ status: BILL_STATUS.PAID })}
              >
                Record payment
              </Dropdown.Item>
            </LockedUi>
            {onEdit && (
              <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                <Dropdown.Item onSelect={onEdit}>Edit</Dropdown.Item>
              </LockedUi>
            )}
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Delete" />}
            >
              <Dropdown.Item type="danger" onSelect={onDelete}>
                Delete
              </Dropdown.Item>
            </LockedUi>
          </>
        );
      }
      case BILL_STATUS.PAID: {
        return (
          <>
            {isBill &&
              bill.withholding_status === WITHHOLDING_STATUS.WITHHELD && (
                <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                  <Dropdown.Item
                    onSelect={() =>
                      onStatusChange({ status: BILL_STATUS.PAID })
                    }
                  >
                    Record IRS payment
                  </Dropdown.Item>
                </LockedUi>
              )}
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
          </>
        );
      }
      case BILL_STATUS.IN_REVIEW: {
        return (
          <>
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
          </>
        );
      }
      case BILL_STATUS.APPROVED: {
        return (
          <>
            <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
              <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
            </LockedUi>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Duplicate" />}
            >
              <Dropdown.Item onSelect={onDuplicate}>Duplicate</Dropdown.Item>
            </LockedUi>
          </>
        );
      }
    }
  })();

  return <>{statusItems}</>;
};
